import React,{useState,useMemo}from'react';
import Pagination from'./Pagination';
import SearchBar,{multiSearch}from'./SearchBar';
import EmptyState from'./EmptyState';
import{TableSkeleton}from'./LoadingState';

interface Column {
  key: string;
  label: string;
  width?: string;
  render?: (row: any, val?: any) => React.ReactNode;
  searchable?: boolean;
  sortable?: boolean;
}

interface Props {
  columns: Column[];
  data: any[];
  loading?: boolean;
  pageSize?: number;
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyMessage?: string;
  emptyDesc?: string;
  emptyIcon?: string;
  emptyAction?: { label: string; onClick: () => void };
  extraFilters?: React.ReactNode;
  rowKey?: string;
  onRowClick?: (row: any) => void;
  stickyHeader?: boolean;
  hideSearch?: boolean;
}

export default function DataTable({
  columns, data, loading=false, pageSize=20, searchPlaceholder='Search...',
  emptyTitle, emptyMessage, emptyDesc='', emptyIcon, emptyAction,
  extraFilters, rowKey='id', onRowClick, stickyHeader=true, hideSearch=false,
}: Props) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');

  const searchFields = columns.filter(c => c.searchable !== false).map(c => c.key);
  const displayEmpty = emptyTitle || emptyMessage || 'No records found';

  const processed = useMemo(() => {
    let d = multiSearch(data, query, searchFields);
    if (sortKey) {
      d = [...d].sort((a, b) => {
        const va = a[sortKey] ?? '';
        const vb = b[sortKey] ?? '';
        const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return d;
  }, [data, query, sortKey, sortDir]);

  const paged = processed.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleSearch = (q: string) => { setQuery(q); setPage(1); };

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      {!hideSearch && (
        <div className="flex items-center gap-3 p-3 border-b bg-gray-50/50 flex-wrap">
          <SearchBar placeholder={searchPlaceholder} onSearch={handleSearch} className="flex-1 min-w-48"/>
          {extraFilters}
          <span className="text-xs text-gray-400 whitespace-nowrap">{processed.length} of {data.length} records</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-max">
          <thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
            <tr style={{ background: '#02357A' }}>
              {columns.map(col => (
                <th key={col.key}
                  className={`px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap select-none ${col.sortable !== false ? 'cursor-pointer hover:bg-[#015CA6]' : ''}`}
                  style={col.width ? { width: col.width } : {}}
                  onClick={() => col.sortable !== false && handleSort(col.key)}>
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.sortable !== false && sortKey === col.key && (
                      <span className="text-white/70">{sortDir === 'asc' ? '↑' : '↓'}</span>
                    )}
                    {col.sortable !== false && sortKey !== col.key && (
                      <span className="text-white/30">↕</span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={columns.length}><TableSkeleton rows={5} cols={columns.length}/></td></tr>
            )}
            {!loading && paged.length === 0 && (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState
                    icon={emptyIcon || '📋'}
                    title={query ? `No results for "${query}"` : displayEmpty}
                    description={query ? 'Try a different search term' : emptyDesc}
                    action={!query ? emptyAction : undefined}
                  />
                </td>
              </tr>
            )}
            {!loading && paged.map((row, idx) => (
              <tr key={row[rowKey] ?? idx}
                onClick={() => onRowClick?.(row)}
                className={`hover:bg-blue-50/30 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${idx % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                {columns.map(col => (
                  <td key={col.key} className="px-4 py-3 align-middle">
                    {col.render
                      // ✅ Pass full row as first arg — all render:(r)=>... functions expect this
                      ? col.render(row, row[col.key])
                      : <span className="text-gray-800">{row[col.key] ?? '—'}</span>
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && processed.length > pageSize && (
        <Pagination total={processed.length} page={page} pageSize={pageSize} onChange={setPage}/>
      )}
    </div>
  );
}
